
<div class="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4 font-sarabun">
    
    <!-- Logo / Brand -->
    <div class="mb-8 text-center animate-fade-in-up">
        <div class="w-16 h-16 bg-emerald-600 rounded-2xl flex items-center justify-center text-white text-3xl mx-auto shadow-lg shadow-emerald-500/30">
            <i class="fa-solid fa-leaf"></i>
        </div>
        <h1 class="mt-4 text-2xl font-bold text-slate-800 tracking-tight font-brand">CHUAMKAN</h1>
        <p class="text-slate-500 text-sm">Medical Certificate Verification</p>
    </div>

    <!-- Card -->
    <div class="bg-white w-full max-w-md rounded-2xl shadow-xl overflow-hidden border border-slate-100 animate-fade-in-up" style="animation-delay: 100ms;">
        
        @if (isLoading()) {
            <div class="p-12 text-center">
                <i class="fa-solid fa-circle-notch animate-spin text-4xl text-emerald-500 mb-4"></i>
                <p class="text-slate-600 font-medium">กำลังตรวจสอบข้อมูล...</p>
                <p class="text-xs text-slate-400 mt-1">Verifying Certificate Status</p>
            </div>
        } @else {
            @if (isValid()) {
                <!-- VALID STATE -->
                <div class="bg-emerald-500 p-6 text-center text-white relative overflow-hidden">
                    <div class="absolute inset-0 opacity-10 pattern-dots"></div>
                    <div class="relative z-10">
                        <div class="w-16 h-16 bg-white rounded-full flex items-center justify-center text-emerald-500 text-3xl mx-auto mb-3 shadow-md">
                            <i class="fa-solid fa-check"></i>
                        </div>
                        <h2 class="text-xl font-bold">เอกสารถูกต้อง (Valid)</h2>
                        <p class="text-emerald-100 text-sm opacity-90">Verified by CHUAMKAN Platform</p>
                    </div>
                </div>

                <div class="p-6 space-y-6">
                    <!-- Status Badge -->
                    <div class="flex justify-center">
                        <span class="px-4 py-1.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold uppercase tracking-wider border border-emerald-200">
                             Status: {{ certData()?.status }}
                        </span>
                    </div>

                    <!-- Details -->
                    <div class="space-y-4">
                        <div class="flex justify-between items-center border-b border-slate-100 pb-3">
                            <span class="text-xs text-slate-500 font-bold uppercase">Certificate ID</span>
                            <span class="text-sm font-mono font-bold text-slate-800">{{ certData()?.id?.substring(0,8) | uppercase }}</span>
                        </div>
                        <div class="flex justify-between items-center border-b border-slate-100 pb-3">
                            <span class="text-xs text-slate-500 font-bold uppercase">Issued Date</span>
                            <span class="text-sm font-bold text-slate-800">{{ certData()?.issueDate | date:'dd MMM yyyy' }}</span>
                        </div>
                        
                        <!-- ADDED: Expiration Date -->
                        @if (certData()?.expiredAt) {
                            <div class="flex justify-between items-center border-b border-slate-100 pb-3">
                                <span class="text-xs text-slate-500 font-bold uppercase">Expired Date</span>
                                <span class="text-sm font-bold text-red-600">{{ certData()?.expiredAt | date:'dd MMM yyyy' }}</span>
                            </div>
                        }

                        <div class="flex justify-between items-center border-b border-slate-100 pb-3">
                            <span class="text-xs text-slate-500 font-bold uppercase">Doctor</span>
                            <span class="text-sm font-bold text-blue-700">{{ certData()?.doctorName }}</span>
                        </div>
                        <div class="flex justify-between items-center border-b border-slate-100 pb-3">
                            <span class="text-xs text-slate-500 font-bold uppercase">Clinic</span>
                            <span class="text-sm font-bold text-slate-700 text-right">{{ certData()?.clinicName }}</span>
                        </div>
                        <div class="flex justify-between items-center pt-1">
                            <span class="text-xs text-slate-500 font-bold uppercase">Patient</span>
                            <span class="text-sm font-bold text-slate-800">{{ certData()?.patientNameMasked }}</span>
                        </div>
                    </div>

                    <!-- New: Issuer Details -->
                    <div class="mt-8 w-full border-t border-slate-200 pt-6">
                        <h3 class="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3 text-center">ออกโดยสถานพยาบาล (Issuer)</h3>
                        <div class="bg-slate-50 p-4 rounded-xl text-sm space-y-2 border border-slate-200 text-center">
                            <p class="font-bold text-slate-800 text-base">{{ certData()?.clinicName }}</p>
                            
                            @if(certData()?.clinicLicense) {
                                <p class="text-slate-500 text-xs"><i class="fa-solid fa-file-medical text-emerald-500 mr-1"></i>เลขที่ใบอนุญาต: {{ certData()?.clinicLicense }}</p>
                            }
                            
                            @if(certData()?.clinicAddress) {
                                <p class="text-slate-500 text-xs leading-relaxed"><i class="fa-solid fa-location-dot text-blue-500 mr-1"></i>{{ certData()?.clinicAddress }}</p>
                            }
                            
                            @if(certData()?.clinicPhone) {
                                <a [href]="'tel:' + certData()?.clinicPhone" class="inline-block mt-2 bg-white border border-slate-300 px-4 py-2 rounded-lg text-slate-700 font-bold hover:bg-slate-100 transition-colors text-xs">
                                    <i class="fa-solid fa-phone mr-2 text-indigo-500"></i> {{ certData()?.clinicPhone }}
                                </a>
                            }
                        </div>
                    </div>

                </div>

            } @else {
                <!-- INVALID STATE -->
                <div class="bg-red-500 p-6 text-center text-white relative overflow-hidden">
                    <div class="absolute inset-0 opacity-10 pattern-lines"></div>
                    <div class="relative z-10">
                        <div class="w-16 h-16 bg-white rounded-full flex items-center justify-center text-red-500 text-3xl mx-auto mb-3 shadow-md">
                            <i class="fa-solid fa-xmark"></i>
                        </div>
                        <h2 class="text-xl font-bold">ไม่พบข้อมูล (Invalid)</h2>
                        <p class="text-red-100 text-sm opacity-90">Certificate Not Found or Revoked</p>
                    </div>
                </div>
                
                <div class="p-8 text-center">
                    <p class="text-slate-600 font-medium mb-2">{{ errorMessage() }}</p>
                    <p class="text-xs text-slate-400">กรุณาตรวจสอบลิงก์ หรือติดต่อเจ้าหน้าที่</p>
                </div>
            }
        }
    </div>

</div>

<style>
    @keyframes fade-in-up {
        from { opacity: 0; transform: translateY(20px); }
        to { opacity: 1; transform: translateY(0); }
    }
    .animate-fade-in-up {
        animation: fade-in-up 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards;
    }
    .pattern-dots {
        background-image: radial-gradient(currentColor 1px, transparent 1px);
        background-size: 10px 10px;
    }
    .pattern-lines {
        background: repeating-linear-gradient(45deg, transparent, transparent 5px, currentColor 5px, currentColor 10px);
    }
</style>
